/**
 * DurableAudioCapture — buffer-first audio capture with reconnect, replay,
 * offline fallback, and crash recovery.
 *
 * Design (specs/AUDIO-DURABILITY.md):
 *  - Every PCM chunk gets a monotonic sequence number and is written to an
 *    encrypted IndexedDB buffer BEFORE any network send attempt.
 *  - WebSocket protocol v2: hello handshake + seq-framed binary + server ACKs.
 *  - On disconnect: exponential backoff reconnect, replay un-ACKed chunks.
 *  - If transcription is unreachable, recording continues locally; on stop the
 *    session is marked pending_upload for batch recovery.
 *  - On app load, findOrphanedRecordings() surfaces crashed sessions.
 *
 * PHI: buffered chunks are AES-GCM encrypted with a per-encounter key fetched
 * from the server (never persisted on device). Chunks are purged on ACK (lazy),
 * sessions purged on clean stop / successful recovery, hard 24h TTL sweep.
 */

import { TranscriptEvent } from './audioCapture'

// ============================================================================
// Types
// ============================================================================

export type ConnectionStatus = 'live' | 'buffering' | 'offline' | 'stopped'

export interface DurableAudioOptions {
  encounterId: string
  token: string
  recoveryKeyB64: string // from GET /encounters/{id}/recovery-key (or local key for offline mode)
  onTranscript: (event: TranscriptEvent) => void
  onStatus: (status: ConnectionStatus) => void
  onError: (error: Error) => void
  onAudioLevel?: (level: number) => void
  /** Offline mode: never attempt the transcription WebSocket; buffer locally only. */
  localOnly?: boolean
}

export interface OrphanedSession {
  encounterId: string
  startedAt: number
  chunkCount: number
  approxSeconds: number
}

interface SessionMeta {
  encounterId: string
  startedAt: number
  lastSeq: number
  lastAckedSeq: number
  closedCleanly: boolean
  updatedAt: number
}

const TARGET_SAMPLE_RATE = 16000
const DB_NAME = 'scribey_audio'
const DB_VERSION = 1
const CHUNK_STORE = 'chunks'
const SESSION_STORE = 'sessions'
const SESSION_TTL_MS = 24 * 60 * 60 * 1000 // 24h hard TTL
const ACK_PURGE_INTERVAL_MS = 5000
const IDB_FLUSH_INTERVAL_MS = 1000
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 8000
const OFFLINE_RETRY_MS = 15000
// ~8MB parts keep us under nginx client_max_body_size 10m
const RECOVERY_PART_BYTES = 8 * 1024 * 1024

// ============================================================================
// IndexedDB helpers
// ============================================================================

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        db.createObjectStore(CHUNK_STORE, { keyPath: ['encounterId', 'seq'] })
      }
      if (!db.objectStoreNames.contains(SESSION_STORE)) {
        db.createObjectStore(SESSION_STORE, { keyPath: 'encounterId' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbPut(db: IDBDatabase, store: string, value: any): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite')
    tx.objectStore(store).put(value)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function idbGet<T>(db: IDBDatabase, store: string, key: any): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly')
    const req = tx.objectStore(store).get(key)
    req.onsuccess = () => resolve(req.result as T | undefined)
    req.onerror = () => reject(req.error)
  })
}

function idbGetAll<T>(db: IDBDatabase, store: string, range?: IDBKeyRange): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly')
    const req = tx.objectStore(store).getAll(range)
    req.onsuccess = () => resolve(req.result as T[])
    req.onerror = () => reject(req.error)
  })
}

function idbDeleteRange(db: IDBDatabase, store: string, range: IDBKeyRange): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite')
    tx.objectStore(store).delete(range)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// ============================================================================
// Crypto helpers (AES-GCM 256, key supplied by server, never persisted)
// ============================================================================

async function importKey(keyB64: string): Promise<CryptoKey> {
  const raw = Uint8Array.from(atob(keyB64), (c) => c.charCodeAt(0))
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

async function encryptChunk(key: CryptoKey, pcm: Int16Array): Promise<{ iv: Uint8Array; data: ArrayBuffer }> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, pcm.buffer as ArrayBuffer)
  return { iv, data }
}

async function decryptChunk(key: CryptoKey, iv: Uint8Array, data: ArrayBuffer): Promise<Int16Array> {
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, data)
  return new Int16Array(plain)
}

// ============================================================================
// Audio helpers
// ============================================================================

/** Downsample float32 @ inputRate to int16 @ 16kHz mono. */
function toPCM16k(samples: Float32Array, inputRate: number): Int16Array {
  let floats = samples
  if (inputRate !== TARGET_SAMPLE_RATE) {
    const ratio = inputRate / TARGET_SAMPLE_RATE
    const outLen = Math.floor(samples.length / ratio)
    const out = new Float32Array(outLen)
    for (let i = 0; i < outLen; i++) {
      // simple average-pooling resample — adequate for speech
      const start = Math.floor(i * ratio)
      const end = Math.min(samples.length, Math.floor((i + 1) * ratio))
      let sum = 0
      for (let j = start; j < end; j++) sum += samples[j]
      out[i] = end > start ? sum / (end - start) : samples[start] || 0
    }
    floats = out
  }
  const pcm = new Int16Array(floats.length)
  for (let i = 0; i < floats.length; i++) {
    const s = Math.max(-1, Math.min(1, floats[i]))
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return pcm
}

function rms(samples: Float32Array): number {
  let sum = 0
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i]
  return Math.sqrt(sum / samples.length)
}

// ============================================================================
// DurableAudioCapture
// ============================================================================

export class DurableAudioCapture {
  private options: DurableAudioOptions
  private stream: MediaStream | null = null
  private audioContext: AudioContext | null = null
  private workletNode: AudioWorkletNode | null = null
  private scriptProcessor: ScriptProcessorNode | null = null
  private socket: WebSocket | null = null
  private db: IDBDatabase | null = null
  private cryptoKey: CryptoKey | null = null

  private isRecording = false
  private isStopping = false
  private seq = 0
  private lastAckedSeq = 0
  private status: ConnectionStatus = 'stopped'

  // chunks not yet flushed to IndexedDB: seq -> pcm
  private pendingIdb: Array<{ seq: number; pcm: Int16Array }> = []
  // chunks sent but not yet acked stay in IndexedDB until ack purge
  private flushTimer: number | null = null
  private purgeTimer: number | null = null
  private reconnectTimer: number | null = null
  private reconnectAttempt = 0
  private helloAcked = false
  // Server confirms 'stop_complete' after draining Deepgram — only then is it
  // safe to finalize the note (prevents transcript truncation on end-note).
  private stopComplete = false
  // Set when the server rejects our token. Reconnecting with the same dead
  // token is pointless — keep buffering locally and surface the error instead.
  private authFailed = false

  constructor(options: DurableAudioOptions) {
    this.options = options
  }

  // --------------------------------------------------------------------
  // Public API
  // --------------------------------------------------------------------

  async start(): Promise<void> {
    try {
      this.db = await openDb()
      await DurableAudioCapture.ttlSweep(this.db)
      this.cryptoKey = await importKey(this.options.recoveryKeyB64)

      // Resume from a previous session for this encounter (e.g. reconnect-resume)
      const existing = await idbGet<SessionMeta>(this.db, SESSION_STORE, this.options.encounterId)
      if (existing && !existing.closedCleanly) {
        this.seq = existing.lastSeq
        this.lastAckedSeq = existing.lastAckedSeq
      }

      await this.writeSessionMeta(false)

      // Microphone
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: TARGET_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      this.audioContext = new AudioContext()
      const source = this.audioContext.createMediaStreamSource(this.stream)

      // Prefer AudioWorklet (off-main-thread); fall back to ScriptProcessor
      let workletOk = false
      try {
        await this.audioContext.audioWorklet.addModule('/pcm-worklet.js')
        this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-capture')
        this.workletNode.port.onmessage = (e: MessageEvent) => {
          if (!this.isRecording) return
          const { samples, sampleRate } = e.data as { samples: Float32Array; sampleRate: number }
          this.handleSamples(samples, sampleRate)
        }
        source.connect(this.workletNode)
        // Worklet needs a destination connection in some browsers to run
        this.workletNode.connect(this.audioContext.destination)
        workletOk = true
      } catch (err) {
        console.warn('AudioWorklet unavailable, falling back to ScriptProcessor:', err)
      }

      if (!workletOk) {
        const proc = this.audioContext.createScriptProcessor(4096, 1, 1)
        proc.onaudioprocess = (e) => {
          if (!this.isRecording) return
          const input = e.inputBuffer.getChannelData(0)
          this.handleSamples(new Float32Array(input), this.audioContext!.sampleRate)
        }
        source.connect(proc)
        proc.connect(this.audioContext.destination)
        this.scriptProcessor = proc
      }

      this.isRecording = true
      this.startFlushLoop()
      this.startPurgeLoop()

      // Network is best-effort — recording is already durable locally
      if (this.options.localOnly) {
        this.setStatus('offline')
      } else {
        this.connect()
      }
    } catch (error) {
      this.options.onError(error as Error)
      throw error
    }
  }

  pause(): void {
    this.isRecording = false
  }

  resume(): void {
    this.isRecording = true
  }

  isActive(): boolean {
    return this.isRecording
  }

  getStatus(): ConnectionStatus {
    return this.status
  }

  /** Number of chunks recorded but not yet ACKed by the server. */
  getUnsentCount(): number {
    return this.seq - this.lastAckedSeq
  }

  /**
   * Stop recording. Returns { delivered } — false means un-ACKed audio remains
   * buffered locally and recoverAudio() should be called to batch-transcribe it.
   */
  async stop(): Promise<{ delivered: boolean }> {
    this.isRecording = false
    this.isStopping = true
    this.stopTimers()

    // Tear down audio graph
    if (this.workletNode) {
      try { this.workletNode.disconnect() } catch {}
      this.workletNode = null
    }
    if (this.scriptProcessor) {
      try { this.scriptProcessor.disconnect() } catch {}
      this.scriptProcessor = null
    }
    if (this.audioContext) {
      try { await this.audioContext.close() } catch {}
      this.audioContext = null
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop())
      this.stream = null
    }

    // Flush remaining chunks to IndexedDB
    await this.flushToIdb()

    // Send stop, then wait for the server to drain Deepgram and confirm with
    // 'stop_complete'. Late transcripts keep flowing to onTranscript during
    // this window — this is what prevents end-note truncation after a
    // connection drop + reconnect (queued chunks are still transcribing).
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify({ type: 'stop' }))
      } catch {}
      // Wait up to 25s for the server-side drain (Deepgram finish can take a
      // few seconds when chunks were replayed after a reconnect).
      const deadline = Date.now() + 25000
      while (!this.stopComplete && Date.now() < deadline) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) break
        await new Promise((r) => setTimeout(r, 150))
      }
      try { this.socket.close() } catch {}
    }
    this.socket = null

    const delivered = this.lastAckedSeq >= this.seq
    if (delivered) {
      await this.cleanupSession()
    } else {
      // leave buffer in place, mark session dirty for recovery
      await this.writeSessionMeta(false)
    }
    this.setStatus('stopped')
    return { delivered }
  }

  /**
   * Batch-upload locally buffered, un-ACKed audio for this encounter to the
   * recovery endpoint. Resolves when the server has transcribed it.
   */
  async recoverAudio(onProgress?: (pct: number) => void): Promise<void> {
    if (!this.db) this.db = await openDb()
    if (!this.cryptoKey) this.cryptoKey = await importKey(this.options.recoveryKeyB64)
    await DurableAudioCapture.uploadBuffered(
      this.db,
      this.cryptoKey,
      this.options.encounterId,
      this.options.token,
      this.lastAckedSeq,
      onProgress,
    )
    await this.cleanupSession()
  }

  // --------------------------------------------------------------------
  // Static: crash recovery + maintenance
  // --------------------------------------------------------------------

  /** List sessions that were not closed cleanly (crash/tab-close orphans). */
  static async findOrphanedRecordings(): Promise<OrphanedSession[]> {
    const db = await openDb()
    try {
      await DurableAudioCapture.ttlSweep(db)
      const sessions = await idbGetAll<SessionMeta>(db, SESSION_STORE)
      const orphans: OrphanedSession[] = []
      for (const s of sessions) {
        if (s.closedCleanly) continue
        const unsent = s.lastSeq - s.lastAckedSeq
        if (unsent <= 0) continue
        orphans.push({
          encounterId: s.encounterId,
          startedAt: s.startedAt,
          chunkCount: unsent,
          // each chunk ≈ 4096 samples at source rate; post-resample ~0.256s at 16kHz/4096
          approxSeconds: Math.round((unsent * 4096) / TARGET_SAMPLE_RATE),
        })
      }
      return orphans
    } finally {
      db.close()
    }
  }

  /**
   * Recover an orphaned session found by findOrphanedRecordings().
   * Fetches the recovery key via the provided fetcher, uploads, transcribes, purges.
   */
  static async recoverOrphan(
    encounterId: string,
    token: string,
    recoveryKeyB64: string,
    onProgress?: (pct: number) => void,
  ): Promise<void> {
    const db = await openDb()
    try {
      const meta = await idbGet<SessionMeta>(db, SESSION_STORE, encounterId)
      if (!meta) return
      const key = await importKey(recoveryKeyB64)
      await DurableAudioCapture.uploadBuffered(db, key, encounterId, token, meta.lastAckedSeq, onProgress)
      await DurableAudioCapture.purgeSession(db, encounterId)
    } finally {
      db.close()
    }
  }

  /**
   * Recover a locally buffered session into a DIFFERENT encounter id.
   * Used for offline-started dictations (local 'offline-*' id) that get a
   * real server encounter once connectivity returns.
   */
  static async recoverOrphanTo(
    sourceEncounterId: string,
    targetEncounterId: string,
    token: string,
    recoveryKeyB64: string,
    onProgress?: (pct: number) => void,
  ): Promise<void> {
    const db = await openDb()
    try {
      const meta = await idbGet<SessionMeta>(db, SESSION_STORE, sourceEncounterId)
      if (!meta) return
      const key = await importKey(recoveryKeyB64)
      await DurableAudioCapture.uploadBuffered(
        db, key, sourceEncounterId, token, meta.lastAckedSeq, onProgress, targetEncounterId,
      )
      await DurableAudioCapture.purgeSession(db, sourceEncounterId)
    } finally {
      db.close()
    }
  }

  /** Discard an orphaned session's local buffer without transcribing. */
  static async discardOrphan(encounterId: string): Promise<void> {
    const db = await openDb()
    try {
      await DurableAudioCapture.purgeSession(db, encounterId)
    } finally {
      db.close()
    }
  }

  private static async ttlSweep(db: IDBDatabase): Promise<void> {
    try {
      const sessions = await idbGetAll<SessionMeta>(db, SESSION_STORE)
      const now = Date.now()
      for (const s of sessions) {
        if (now - (s.updatedAt || s.startedAt) > SESSION_TTL_MS) {
          await DurableAudioCapture.purgeSession(db, s.encounterId)
        }
      }
    } catch (err) {
      console.warn('TTL sweep failed:', err)
    }
  }

  private static async purgeSession(db: IDBDatabase, encounterId: string): Promise<void> {
    await idbDeleteRange(
      db,
      CHUNK_STORE,
      IDBKeyRange.bound([encounterId, 0], [encounterId, Number.MAX_SAFE_INTEGER]),
    )
    await idbDeleteRange(db, SESSION_STORE, IDBKeyRange.only(encounterId))
  }

  private static async uploadBuffered(
    db: IDBDatabase,
    key: CryptoKey,
    encounterId: string,
    token: string,
    afterSeq: number,
    onProgress?: (pct: number) => void,
    uploadAsId?: string,
  ): Promise<void> {
    const targetId = uploadAsId || encounterId
    const rows = await idbGetAll<{ encounterId: string; seq: number; iv: Uint8Array; data: ArrayBuffer }>(
      db,
      CHUNK_STORE,
      IDBKeyRange.bound([encounterId, afterSeq + 1], [encounterId, Number.MAX_SAFE_INTEGER]),
    )
    if (rows.length === 0) return
    rows.sort((a, b) => a.seq - b.seq)

    // Decrypt and concatenate into PCM parts ≤ RECOVERY_PART_BYTES
    const parts: Blob[] = []
    let current: Int16Array[] = []
    let currentBytes = 0
    for (const row of rows) {
      const pcm = await decryptChunk(key, row.iv, row.data)
      current.push(pcm)
      currentBytes += pcm.byteLength
      if (currentBytes >= RECOVERY_PART_BYTES) {
        parts.push(new Blob(current.map((p) => p.buffer as ArrayBuffer), { type: 'application/octet-stream' }))
        current = []
        currentBytes = 0
      }
    }
    if (current.length) {
      parts.push(new Blob(current.map((p) => p.buffer as ArrayBuffer), { type: 'application/octet-stream' }))
    }

    for (let i = 0; i < parts.length; i++) {
      const form = new FormData()
      form.append('audio', parts[i], `recovery_${i}.pcm`)
      form.append('part_index', String(i))
      form.append('final', String(i === parts.length - 1))
      const res = await fetch(`/api/v1/encounters/${targetId}/recover-audio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.detail || `Recovery upload failed (part ${i}): ${res.status}`)
      }
      onProgress?.(Math.round(((i + 1) / parts.length) * 100))
    }
  }

  // --------------------------------------------------------------------
  // Internals: capture path
  // --------------------------------------------------------------------

  private handleSamples(samples: Float32Array, inputRate: number): void {
    if (this.options.onAudioLevel) this.options.onAudioLevel(rms(samples))

    const pcm = toPCM16k(samples, inputRate)
    if (pcm.length === 0) return

    this.seq += 1
    const seq = this.seq

    // 1. Buffer first (memory queue, flushed to IndexedDB on a short timer)
    this.pendingIdb.push({ seq, pcm })

    // 2. Network second
    this.trySend(seq, pcm)
  }

  private trySend(seq: number, pcm: Int16Array): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN && this.helloAcked) {
      try {
        const framed = new ArrayBuffer(4 + pcm.byteLength)
        new DataView(framed).setUint32(0, seq, true)
        new Uint8Array(framed, 4).set(new Uint8Array(pcm.buffer as ArrayBuffer, pcm.byteOffset, pcm.byteLength))
        this.socket.send(framed)
      } catch (err) {
        console.warn('Send failed, chunk stays buffered:', err)
      }
    }
  }

  private startFlushLoop(): void {
    this.flushTimer = window.setInterval(() => {
      void this.flushToIdb()
    }, IDB_FLUSH_INTERVAL_MS)
  }

  private async flushToIdb(): Promise<void> {
    if (!this.db || !this.cryptoKey || this.pendingIdb.length === 0) return
    const batch = this.pendingIdb.splice(0, this.pendingIdb.length)
    try {
      for (const { seq, pcm } of batch) {
        // Skip chunks already acked by the time we flush
        if (seq <= this.lastAckedSeq) continue
        const { iv, data } = await encryptChunk(this.cryptoKey, pcm)
        await idbPut(this.db, CHUNK_STORE, {
          encounterId: this.options.encounterId,
          seq,
          iv,
          data,
        })
      }
      await this.writeSessionMeta(false)
    } catch (err) {
      console.error('IndexedDB flush failed:', err)
      // Put the batch back so we retry next tick (memory still holds the audio)
      this.pendingIdb.unshift(...batch)
    }
  }

  private startPurgeLoop(): void {
    this.purgeTimer = window.setInterval(() => {
      void this.purgeAcked()
    }, ACK_PURGE_INTERVAL_MS)
  }

  private async purgeAcked(): Promise<void> {
    if (!this.db || this.lastAckedSeq <= 0) return
    try {
      await idbDeleteRange(
        this.db,
        CHUNK_STORE,
        IDBKeyRange.bound([this.options.encounterId, 0], [this.options.encounterId, this.lastAckedSeq]),
      )
    } catch (err) {
      console.warn('Ack purge failed:', err)
    }
  }

  private async writeSessionMeta(closedCleanly: boolean): Promise<void> {
    if (!this.db) return
    const meta: SessionMeta = {
      encounterId: this.options.encounterId,
      startedAt: Date.now(),
      lastSeq: this.seq,
      lastAckedSeq: this.lastAckedSeq,
      closedCleanly,
      updatedAt: Date.now(),
    }
    const existing = await idbGet<SessionMeta>(this.db, SESSION_STORE, this.options.encounterId)
    if (existing) meta.startedAt = existing.startedAt
    await idbPut(this.db, SESSION_STORE, meta)
  }

  private async cleanupSession(): Promise<void> {
    if (!this.db) return
    await DurableAudioCapture.purgeSession(this.db, this.options.encounterId)
  }

  // --------------------------------------------------------------------
  // Internals: network path
  // --------------------------------------------------------------------

  private connect(): void {
    if (this.isStopping || this.authFailed) return
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${protocol}//${window.location.host}/api/v1/ws/transcribe/${this.options.encounterId}?token=${this.options.token}`

    this.helloAcked = false
    let socket: WebSocket
    try {
      socket = new WebSocket(url)
    } catch (err) {
      this.scheduleReconnect()
      return
    }
    this.socket = socket

    const connectTimeout = window.setTimeout(() => {
      if (socket.readyState !== WebSocket.OPEN) {
        try { socket.close() } catch {}
      }
    }, 10000)

    socket.onopen = () => {
      clearTimeout(connectTimeout)
      // Protocol v2 handshake: announce last acked seq so the server knows
      // we'll replay everything after it.
      try {
        socket.send(JSON.stringify({ type: 'hello', proto: 2, last_seq: this.lastAckedSeq }))
      } catch {}
    }

    socket.onmessage = (event) => {
      let data: any
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }

      if (data.type === 'ack') {
        if (typeof data.seq === 'number' && data.seq > this.lastAckedSeq) {
          this.lastAckedSeq = data.seq
        }
        return
      }

      if (data.type === 'stop_complete') {
        this.stopComplete = true
        return
      }

      if (data.type === 'ready') {
        // Server accepted hello + Deepgram is up: replay un-ACKed buffer, go live
        this.helloAcked = true
        this.reconnectAttempt = 0
        this.setStatus('live')
        void this.replayUnacked()
        this.options.onTranscript(data)
        return
      }

      if (data.type === 'error') {
        // Auth errors are terminal for this token — do NOT reconnect-loop.
        // Audio keeps buffering locally; the page can prompt re-login and the
        // buffered session is recoverable afterwards (orphan recovery flow).
        if (typeof data.message === 'string' && /auth/i.test(data.message)) {
          this.authFailed = true
          this.setStatus('offline')
          this.options.onError(new Error('Your session expired. Recording continues locally — please sign in again to resume live transcription.'))
        } else {
          this.options.onError(new Error(data.message || 'Transcription error'))
        }
        return
      }

      this.options.onTranscript(data as TranscriptEvent)
    }

    socket.onerror = () => {
      // onclose will follow; let it drive reconnect
    }

    socket.onclose = () => {
      clearTimeout(connectTimeout)
      if (this.socket === socket) this.socket = null
      this.helloAcked = false
      if (this.isRecording && !this.isStopping && !this.authFailed) {
        this.scheduleReconnect()
      }
    }
  }

  private async replayUnacked(): Promise<void> {
    if (!this.db || !this.cryptoKey) return
    // Make sure everything recorded so far is durably flushed first
    await this.flushToIdb()
    try {
      const rows = await idbGetAll<{ seq: number; iv: Uint8Array; data: ArrayBuffer }>(
        this.db,
        CHUNK_STORE,
        IDBKeyRange.bound(
          [this.options.encounterId, this.lastAckedSeq + 1],
          [this.options.encounterId, Number.MAX_SAFE_INTEGER],
        ),
      )
      rows.sort((a, b) => a.seq - b.seq)
      for (const row of rows) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) break
        const pcm = await decryptChunk(this.cryptoKey, row.iv, row.data)
        this.trySend(row.seq, pcm)
      }
    } catch (err) {
      console.warn('Replay failed (audio stays buffered):', err)
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.isStopping) return
    this.reconnectAttempt += 1
    const delay =
      this.reconnectAttempt > 8
        ? OFFLINE_RETRY_MS
        : Math.min(RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempt - 1), RECONNECT_MAX_MS)

    this.setStatus(this.reconnectAttempt > 3 ? 'offline' : 'buffering')

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      if (this.isRecording && !this.isStopping) this.connect()
    }, delay)
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return
    this.status = status
    this.options.onStatus(status)
  }

  private stopTimers(): void {
    if (this.flushTimer) { clearInterval(this.flushTimer); this.flushTimer = null }
    if (this.purgeTimer) { clearInterval(this.purgeTimer); this.purgeTimer = null }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
  }
}

/** Check if the browser supports durable audio capture. */
/**
 * Wipe ALL locally-cached PHI from this browser. Used on logout (critical for
 * shared/public computers): deletes the audio IndexedDB and the offline
 * encounter key map so no dictation/audio or crypto keys linger on the machine.
 * Best-effort and never throws.
 */
export async function wipeAllPHI(): Promise<void> {
  try {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME)
      req.onsuccess = () => resolve()
      req.onerror = () => resolve()
      req.onblocked = () => resolve()
      // Safety timeout in case the delete is blocked by an open connection.
      setTimeout(() => resolve(), 1500)
    })
  } catch { /* ignore */ }
  try {
    localStorage.removeItem('scribey_offline_keys')
    localStorage.removeItem('scribey_patients_sort')
    localStorage.removeItem('scribey_intake_sort')
  } catch { /* ignore */ }
}

export function isDurableAudioSupported(): boolean {
  return !!(
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    window.indexedDB &&
    window.crypto?.subtle
  )
}